/**
 * ThemeProvider — SSOT for theme state.
 *
 * Tri-state `'system' | 'light' | 'dark'`, persisted to localStorage and
 * reflected on `<html data-theme="...">`. `'system'` follows
 * `prefers-color-scheme` live. `meta[name=theme-color]` is kept in sync so
 * mobile browsers blend the chrome into the app's background.
 *
 * Exposes `useTheme()` for consumers (UserMenu, Profile Appearance, ⌘K
 * palette). Pure React context — no external store.
 */

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
    useSyncExternalStore,
    type ReactNode,
} from 'react';

export type ThemeChoice = 'system' | 'light' | 'dark';
export type ResolvedTheme = 'light' | 'dark';

type ThemeContextValue = {
    choice: ThemeChoice;
    resolved: ResolvedTheme;
    setChoice: (next: ThemeChoice) => void;
    cycle: () => void; // convenience for a simple toggle button
};

const STORAGE_KEY = 'chiefy.theme';
const ThemeContext = createContext<ThemeContextValue | null>(null);

function readStoredChoice(): ThemeChoice {
    if (typeof window === 'undefined') return 'system';
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw === 'light' || raw === 'dark' || raw === 'system') return raw;
    return 'system';
}

function prefersDark(): boolean {
    if (typeof window === 'undefined' || !window.matchMedia) return true;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

/** useSyncExternalStore subscriber — fires when the OS theme flips.
 *  Drives the `resolved` theme when choice === 'system'. Returns a
 *  cleanup to unsubscribe. */
function subscribeOsTheme(cb: () => void): () => void {
    if (typeof window === 'undefined' || !window.matchMedia) return () => {};
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    mql.addEventListener?.('change', cb);
    return () => mql.removeEventListener?.('change', cb);
}

function getOsTheme(): ResolvedTheme { return prefersDark() ? 'dark' : 'light'; }
function getOsThemeSSR(): ResolvedTheme { return 'dark'; }

const THEME_COLOR: Record<ResolvedTheme, string> = {
    dark: '#120825',
    light: '#f7f5ff',
};

function applyDomTheme(resolved: ResolvedTheme): void {
    if (typeof document === 'undefined') return;
    document.documentElement.setAttribute('data-theme', resolved);
    let meta = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
    if (!meta) {
        meta = document.createElement('meta');
        meta.name = 'theme-color';
        document.head.appendChild(meta);
    }
    meta.content = THEME_COLOR[resolved];
}

export function ThemeProvider({ children }: { children: ReactNode }) {
    const [choice, setChoiceState] = useState<ThemeChoice>(readStoredChoice);
    // Subscribe to the OS theme externally so we derive `resolved` with
    // pure useMemo instead of a setState-in-effect cascade. This is the
    // React 19 idiomatic shape — the value is always consistent with
    // `choice` + the current OS theme on every render.
    const osTheme = useSyncExternalStore(subscribeOsTheme, getOsTheme, getOsThemeSSR);
    const resolved: ResolvedTheme = choice === 'system' ? osTheme : choice;

    // Apply to <html> and meta any time the resolved theme changes.
    useEffect(() => {
        applyDomTheme(resolved);
    }, [resolved]);

    const setChoice = useCallback((next: ThemeChoice) => {
        try {
            window.localStorage.setItem(STORAGE_KEY, next);
        } catch { /* quota / private mode */ }
        setChoiceState(next);
    }, []);

    const cycle = useCallback(() => {
        setChoice(choice === 'system' ? 'light' : choice === 'light' ? 'dark' : 'system');
    }, [choice, setChoice]);

    const value = useMemo<ThemeContextValue>(
        () => ({ choice, resolved, setChoice, cycle }),
        [choice, resolved, setChoice, cycle],
    );

    return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
    const ctx = useContext(ThemeContext);
    if (!ctx) {
        throw new Error('useTheme must be used within <ThemeProvider>');
    }
    return ctx;
}
